import { HTMLAttributes, useState } from 'react';

import { ScaleuiWrapper } from '@scaleaq/scaleui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';

import { defaultNS, i18nInstance } from '@/localization';
import { HubProvider } from '@/providers/HubProvider.tsx';
import { useUserSettingsStore } from '@/stores';

import { AccessibleSitesProvider } from './appCoreProvider/AccessibleSitesProvider.tsx';
import { SiteProvider } from './appCoreProvider/accessibleSitesProvider/SiteProvider.tsx';

/**
 * Single root provider stack for Site Control: renders `ScaleuiWrapper` (theme from user settings),
 * `QueryClientProvider`, `AccessibleSitesProvider` (gates on `/config` and owns the accessible-site
 * list for every `SiteProvider`), `SiteProvider` (includes site-scoped available-services and
 * inventory-data contexts), `I18nextProvider` (shared `i18nInstance` and `defaultNS`), and
 * `HubProvider` for real-time hub context. The host passes only `children`; it does not need to wrap
 * with Scale UI theme shell or `I18nextProvider` separately unless intentionally nesting.
 *
 * @param children - The child components to be wrapped by the providers
 */
export const AppCoreProviders = ({ children }: HTMLAttributes<HTMLDivElement>) => {
  const [queryClient] = useState(() => new QueryClient());
  const theme = useUserSettingsStore(state => state.theme);
  return (
    <ScaleuiWrapper theme={theme}>
      <QueryClientProvider client={queryClient}>
        <AccessibleSitesProvider>
          <SiteProvider>
            <I18nextProvider i18n={i18nInstance} defaultNS={defaultNS}>
              <HubProvider>{children}</HubProvider>
            </I18nextProvider>
          </SiteProvider>
        </AccessibleSitesProvider>
      </QueryClientProvider>
    </ScaleuiWrapper>
  );
};
